import { CVProcessingStages, JobStatus, JobTypes } from "@lib/enums";
import { useJobStatus } from "@lib/jobs/useJobStatus";
import useRecruiterDetails from "@lib/user/useRecruiterDetails";
import { useEffect, useState } from "react";

export default function useIndividualImportingStatus(candidateId, invalidate) {
  const { jobs } = useJobStatus(JobTypes.AI, [
    JobStatus.IMPORTING_CV,
    JobStatus.FAILED,
    JobStatus.COMPLETE,
  ]);
  const { recruiter } = useRecruiterDetails();
  const [summaryArrived, setSummaryArrived] = useState(false);

  // Reset when switching candidates so a fresh import for a new candidate
  // doesn't inherit a stale "already done" flag from the previous one. Done
  // during render (React's replacement for a set-state-in-effect).
  const [prevCandidateId, setPrevCandidateId] = useState(candidateId);
  if (candidateId !== prevCandidateId) {
    setPrevCandidateId(candidateId);
    setSummaryArrived(false);
  }

  const thisJob = jobs?.find(
    (job) => job.metadata?.candidate_id === candidateId
  );

  const isImporting = thisJob?.status === JobStatus.IMPORTING_CV;
  const stages = thisJob?.metadata?.stages_completed || [];

  const isImportingCulture =
    isImporting && !stages.includes(CVProcessingStages.CULTURE);
  const isImportingRoles =
    isImporting && !stages.includes(CVProcessingStages.ROLES);
  const isImportingProjects =
    isImporting && !stages.includes(CVProcessingStages.PROJECTS);
  const isImportingEducation =
    isImporting && !stages.includes(CVProcessingStages.EDUCATION);
  const isImportingSkills =
    isImporting && !stages.includes(CVProcessingStages.SKILLS);

  // The summary is auto-generated after the import job reaches COMPLETE, so
  // there's no stage/status transition to ride on. Treat it as "still working"
  // while a tracked import job exists and hasn't failed, then clear as soon as
  // the API broadcasts CandidateSummaryGenerated for this candidate.
  const isGeneratingSummary =
    !!thisJob && thisJob.status !== JobStatus.FAILED && !summaryArrived;

  // invalidate to refresh candidate data every time the stages changes or importing status changes
  useEffect(() => {
    invalidate();
  }, [isImporting, JSON.stringify(stages)]);

  // The auto-summary lands after the import job is already COMPLETE, so it has
  // no stage/status change to ride on. Refresh the candidate when the API
  // broadcasts that the summary finished.
  const userId = recruiter?.user?.id;
  useEffect(() => {
    if (typeof window === "undefined" || !window.Echo || !userId) {
      return;
    }

    const channel = window.Echo.private(`app.user.${userId}`);
    const handler = (e) => {
      if (e?.candidate_id === candidateId) {
        setSummaryArrived(true);
        invalidate();
      }
    };

    channel.listen("CandidateSummaryGenerated", handler);

    return () => {
      channel.stopListening("CandidateSummaryGenerated", handler);
    };
  }, [userId, candidateId]);

  return {
    isImportingCulture,
    isImportingRoles,
    isImportingProjects,
    isImportingEducation,
    isImportingSkills,
    isGeneratingSummary,
  };
}
